/**
 * FullDocumentDiffViewer — unified document diff with Word formatting preserved.
 *
 * Faithfully ported from INDx prototype's FullDocumentDiffViewer.js.
 * Uses LCS (Longest Common Subsequence) with similarity scoring for block-level
 * alignment, then word-level diff within matched blocks. Handles tables with
 * cell-by-cell diff and lists with item-level alignment.
 */

import { useMemo } from 'react';
import { diffWords } from 'diff';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FullDocumentDiffViewerProps {
  originalText: string;
  modifiedText: string;
  originalHtml?: string;
  modifiedHtml?: string;
  originalLabel?: string;
  modifiedLabel?: string;
}

interface Block {
  type: string;
  element: Element;
  text: string;
  signature: string;
}

interface AlignmentOp {
  type: 'match' | 'delete' | 'insert';
  original?: Block;
  modified?: Block;
  similarity?: number;
}

// ---------------------------------------------------------------------------
// Text Utilities
// ---------------------------------------------------------------------------

function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

function normalizeTableSignature(table: Element): string {
  const cells = table.querySelectorAll('td, th');
  const cellTexts = Array.from(cells).map((c) => normalizeText(c.textContent || ''));
  return `table:${cellTexts.join('|')}`;
}

/** Compute Jaccard similarity between two blocks (0–1). */
function computeSimilarity(block1: Block, block2: Block): number {
  if (block1.type !== block2.type) {
    const textTypes = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'];
    if (!textTypes.includes(block1.type) || !textTypes.includes(block2.type)) {
      return 0;
    }
  }

  const sig1 = block1.signature;
  const sig2 = block2.signature;

  if (sig1 === sig2) return 1;
  if (!sig1 || !sig2) return 0;

  const words1 = new Set(sig1.split(/\s+/));
  const words2 = new Set(sig2.split(/\s+/));

  const intersection = new Set([...words1].filter((w) => words2.has(w)));
  const union = new Set([...words1, ...words2]);

  return union.size > 0 ? intersection.size / union.size : 0;
}

// ---------------------------------------------------------------------------
// Block Extraction
// ---------------------------------------------------------------------------

function extractBlocks(body: Element): Block[] {
  const blocks: Block[] = [];
  for (const child of Array.from(body.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tagName = el.tagName.toLowerCase();
      blocks.push({
        type: tagName,
        element: el,
        text: el.textContent || '',
        signature:
          tagName === 'table'
            ? normalizeTableSignature(el)
            : normalizeText(el.textContent || ''),
      });
    }
  }
  return blocks;
}

// ---------------------------------------------------------------------------
// LCS Block Alignment (similarity-based)
// ---------------------------------------------------------------------------

const SIMILARITY_THRESHOLD = 0.5;

function alignBlocks(originalBlocks: Block[], modifiedBlocks: Block[]): AlignmentOp[] {
  const m = originalBlocks.length;
  const n = modifiedBlocks.length;

  // Build similarity matrix
  const sim: number[][] = [];
  for (let i = 0; i < m; i++) {
    sim[i] = [];
    for (let j = 0; j < n; j++) {
      sim[i][j] = computeSimilarity(originalBlocks[i], modifiedBlocks[j]);
    }
  }

  // Dynamic programming for LCS with similarity scores
  const dp: number[][] = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = new Array(n + 1).fill(0);
  }

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (sim[i - 1][j - 1] >= SIMILARITY_THRESHOLD) {
        dp[i][j] = dp[i - 1][j - 1] + sim[i - 1][j - 1];
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack
  const alignment: AlignmentOp[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (
      i > 0 &&
      j > 0 &&
      sim[i - 1][j - 1] >= SIMILARITY_THRESHOLD &&
      dp[i][j] === dp[i - 1][j - 1] + sim[i - 1][j - 1]
    ) {
      alignment.unshift({
        type: 'match',
        original: originalBlocks[i - 1],
        modified: modifiedBlocks[j - 1],
        similarity: sim[i - 1][j - 1],
      });
      i--;
      j--;
    } else if (i > 0 && (j === 0 || dp[i - 1][j] >= dp[i][j - 1])) {
      alignment.unshift({ type: 'delete', original: originalBlocks[i - 1] });
      i--;
    } else {
      alignment.unshift({ type: 'insert', modified: modifiedBlocks[j - 1] });
      j--;
    }
  }

  return alignment;
}

// ---------------------------------------------------------------------------
// Word-Level Diff
// ---------------------------------------------------------------------------

function computeWordDiff(oldText: string, newText: string) {
  return diffWords(oldText, newText).map((part) => ({
    type: part.added ? ('added' as const) : part.removed ? ('removed' as const) : ('unchanged' as const),
    text: part.value,
  }));
}

// ---------------------------------------------------------------------------
// Rendering Helpers (React components built from DOM nodes)
// ---------------------------------------------------------------------------

let keyCounter = 0;
function nextKey(prefix: string) {
  return `${prefix}-${keyCounter++}`;
}

/** Render inline content preserving bold, italic, underline, etc. */
function renderInlineContent(element: Element): React.ReactNode[] {
  const result: React.ReactNode[] = [];

  const processNode = (node: ChildNode): React.ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const children = Array.from(el.childNodes).map(processNode);
      const key = nextKey('inline');

      switch (tag) {
        case 'strong':
        case 'b':
          return <strong key={key}>{children}</strong>;
        case 'em':
        case 'i':
          return <em key={key}>{children}</em>;
        case 'u':
          return <u key={key}>{children}</u>;
        case 's':
        case 'strike':
          return <s key={key}>{children}</s>;
        case 'sup':
          return <sup key={key}>{children}</sup>;
        case 'sub':
          return <sub key={key}>{children}</sub>;
        case 'a':
          return (
            <span key={key} className="text-blue-600 underline">
              {children}
            </span>
          );
        case 'br':
          return <br key={key} />;
        default:
          return <span key={key}>{children}</span>;
      }
    }
    return null;
  };

  for (const child of Array.from(element.childNodes)) {
    const processed = processNode(child);
    if (processed !== null) {
      result.push(processed);
    }
  }

  return result;
}

/** Render diff parts (added/removed/unchanged) with Tailwind classes. */
function DiffParts({ parts }: { parts: { type: 'added' | 'removed' | 'unchanged'; text: string }[] }) {
  return (
    <>
      {parts.map((part, idx) => {
        if (part.type === 'added') {
          return (
            <span key={idx} className="bg-green-200 text-green-900 px-0.5 rounded-sm font-medium">
              {part.text}
            </span>
          );
        }
        if (part.type === 'removed') {
          return (
            <span key={idx} className="bg-red-200 text-red-900 px-0.5 rounded-sm line-through">
              {part.text}
            </span>
          );
        }
        return <span key={idx}>{part.text}</span>;
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Block-Level Renderers
// ---------------------------------------------------------------------------

const headingClasses: Record<string, string> = {
  h1: 'text-2xl font-semibold text-slate-900 border-b border-slate-200 pb-2 mt-6 mb-3',
  h2: 'text-xl font-semibold text-slate-900 mt-6 mb-3',
  h3: 'text-lg font-semibold text-slate-900 mt-5 mb-2',
  h4: 'text-base font-semibold text-slate-900 mt-4 mb-2',
  h5: 'text-sm font-semibold text-slate-900 mt-4 mb-2',
  h6: 'text-xs font-semibold text-slate-900 mt-4 mb-2',
};

function getBlockClass(tagName: string): string {
  if (headingClasses[tagName]) return headingClasses[tagName];
  if (tagName === 'p') return 'mb-3';
  if (tagName === 'ul' || tagName === 'ol') return 'my-2 pl-6 space-y-1';
  return '';
}

/** Render a table with optional added/deleted styling. */
function RenderTable({
  tableEl,
  diffType,
}: {
  tableEl: Element;
  diffType?: 'added' | 'deleted';
}) {
  const rows = tableEl.querySelectorAll('tr');
  const wrapperClass = diffType === 'added'
    ? 'border-l-4 border-green-500 bg-green-50'
    : diffType === 'deleted'
      ? 'border-l-4 border-red-500 bg-red-50'
      : '';

  const cellBg = diffType === 'added'
    ? 'bg-green-50 text-green-900'
    : diffType === 'deleted'
      ? 'bg-red-50 text-red-900 line-through'
      : '';

  return (
    <table
      key={nextKey('table')}
      className={`border-collapse w-full my-4 ${wrapperClass}`}
    >
      <tbody>
        {Array.from(rows).map((row) => {
          const cells = row.querySelectorAll('td, th');
          return (
            <tr key={nextKey('row')}>
              {Array.from(cells).map((cell) => {
                const isHeader = cell.tagName.toLowerCase() === 'th';
                const Tag = isHeader ? 'th' : 'td';
                const baseCls = `border border-slate-200 px-3 py-2 text-left text-sm ${
                  isHeader ? 'bg-slate-50 font-semibold' : ''
                }`;
                return (
                  <Tag key={nextKey('cell')} className={`${baseCls} ${cellBg}`}>
                    {cell.textContent}
                  </Tag>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Render a matched table with cell-by-cell word diff. */
function RenderMatchedTable({
  originalTable,
  modifiedTable,
}: {
  originalTable: Element;
  modifiedTable: Element;
}) {
  const originalCells = originalTable.querySelectorAll('td, th');
  const modifiedRows = modifiedTable.querySelectorAll('tr');

  // Map original cell contents by flat index
  const originalCellMap = new Map<number, string>();
  originalCells.forEach((cell, idx) => {
    originalCellMap.set(idx, cell.textContent || '');
  });

  let cellIndex = 0;

  return (
    <table key={nextKey('mtable')} className="border-collapse w-full my-4">
      <tbody>
        {Array.from(modifiedRows).map((row) => {
          const cells = row.querySelectorAll('td, th');
          return (
            <tr key={nextKey('row')}>
              {Array.from(cells).map((cell) => {
                const isHeader = cell.tagName.toLowerCase() === 'th';
                const Tag = isHeader ? 'th' : 'td';
                const origText = originalCellMap.get(cellIndex) || '';
                const modText = cell.textContent || '';
                cellIndex++;

                const parts = computeWordDiff(origText, modText);
                const hasChanges = parts.some((p) => p.type !== 'unchanged');

                return (
                  <Tag
                    key={nextKey('cell')}
                    className={`border border-slate-200 px-3 py-2 text-left text-sm ${
                      isHeader ? 'bg-slate-50 font-semibold' : ''
                    } ${hasChanges ? 'bg-amber-50' : ''}`}
                  >
                    <DiffParts parts={parts} />
                  </Tag>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Render list items for an added/deleted list. */
function RenderListItems({
  listEl,
  diffType,
}: {
  listEl: Element;
  diffType?: 'added' | 'deleted';
}) {
  const items = listEl.querySelectorAll(':scope > li');
  const itemClass = diffType === 'added'
    ? 'bg-green-50 text-green-900 px-2 py-1 -ml-2 rounded'
    : diffType === 'deleted'
      ? 'bg-red-50 text-red-900 line-through px-2 py-1 -ml-2 rounded'
      : '';

  return (
    <>
      {Array.from(items).map((li) => (
        <li key={nextKey('li')} className={itemClass}>
          {diffType ? li.textContent : renderInlineContent(li as Element)}
        </li>
      ))}
    </>
  );
}

/** Render a matched list with item-level alignment and word diff. */
function RenderMatchedList({
  originalList,
  modifiedList,
  tagName,
}: {
  originalList: Element;
  modifiedList: Element;
  tagName: string;
}) {
  const origItems = Array.from(originalList.querySelectorAll(':scope > li'));
  const modItems = Array.from(modifiedList.querySelectorAll(':scope > li'));
  const maxLen = Math.max(origItems.length, modItems.length);

  const listContent: React.ReactNode[] = [];

  for (let i = 0; i < maxLen; i++) {
    const origText = i < origItems.length ? origItems[i].textContent || '' : '';
    const modText = i < modItems.length ? modItems[i].textContent || '' : '';

    if (i >= origItems.length) {
      // Added item
      listContent.push(
        <li key={nextKey('li')} className="bg-green-50 text-green-900 px-2 py-1 -ml-2 rounded">
          {modText}
        </li>,
      );
    } else if (i >= modItems.length) {
      // Deleted item
      listContent.push(
        <li key={nextKey('li')} className="bg-red-50 text-red-900 line-through px-2 py-1 -ml-2 rounded">
          {origText}
        </li>,
      );
    } else {
      // Matched item — word diff
      const parts = computeWordDiff(origText, modText);
      listContent.push(
        <li key={nextKey('li')}>
          <DiffParts parts={parts} />
        </li>,
      );
    }
  }

  const ListTag = tagName === 'ol' ? 'ol' : 'ul';
  return (
    <ListTag key={nextKey('list')} className="my-2 pl-6 space-y-1">
      {listContent}
    </ListTag>
  );
}

/** Render a block element preserving its tag structure. */
function renderBlockElement(
  element: Element,
  diffType?: 'added' | 'deleted',
): React.ReactNode {
  const tagName = element.tagName.toLowerCase();
  const key = nextKey('block');

  // Tables get special handling
  if (tagName === 'table') {
    return <RenderTable key={key} tableEl={element} diffType={diffType} />;
  }

  // Lists get special handling
  if (tagName === 'ul' || tagName === 'ol') {
    const diffClass = diffType === 'added'
      ? 'bg-green-50 border-l-4 border-green-500 px-3 py-2 my-2'
      : diffType === 'deleted'
        ? 'bg-red-50 border-l-4 border-red-500 px-3 py-2 my-2 line-through'
        : getBlockClass(tagName);

    const ListTag = tagName === 'ol' ? 'ol' : 'ul';
    return (
      <ListTag key={key} className={diffClass}>
        <RenderListItems listEl={element} diffType={diffType} />
      </ListTag>
    );
  }

  // Text blocks (p, h1–h6, div, etc.)
  const blockClass = getBlockClass(tagName);
  const diffClass = diffType === 'added'
    ? 'bg-green-50 border-l-4 border-green-500 px-3 py-2 my-2 text-green-900'
    : diffType === 'deleted'
      ? 'bg-red-50 border-l-4 border-red-500 px-3 py-2 my-2 text-red-900 line-through'
      : blockClass;

  const content = diffType
    ? element.textContent
    : renderInlineContent(element);

  const Tag = /^h[1-6]$/.test(tagName) ? tagName as keyof React.JSX.IntrinsicElements : 'p';

  return (
    <Tag key={key} className={diffClass}>
      {content}
    </Tag>
  );
}

/** Render a matched block with word-level diff inside. */
function renderMatchedBlock(original: Block, modified: Block): React.ReactNode {
  const tagName = modified.type;
  const key = nextKey('matched');

  // Tables: cell-by-cell diff
  if (tagName === 'table') {
    return (
      <RenderMatchedTable
        key={key}
        originalTable={original.element}
        modifiedTable={modified.element}
      />
    );
  }

  // Lists: item-level alignment
  if (tagName === 'ul' || tagName === 'ol') {
    return (
      <RenderMatchedList
        key={key}
        originalList={original.element}
        modifiedList={modified.element}
        tagName={tagName}
      />
    );
  }

  // Text blocks: word-level diff
  const parts = computeWordDiff(original.text, modified.text);
  const blockClass = getBlockClass(tagName);
  const Tag = /^h[1-6]$/.test(tagName) ? tagName as keyof React.JSX.IntrinsicElements : 'p';

  return (
    <Tag key={key} className={blockClass}>
      <DiffParts parts={parts} />
    </Tag>
  );
}

// ---------------------------------------------------------------------------
// Plain-Text Fallback
// ---------------------------------------------------------------------------

function renderPlainTextDiff(originalText: string, modifiedText: string): React.ReactNode {
  const parts = computeWordDiff(originalText, modifiedText);

  return (
    <pre className="p-5 font-mono text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap break-words">
      <DiffParts parts={parts} />
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function FullDocumentDiffViewer({
  originalText,
  modifiedText,
  originalHtml,
  modifiedHtml,
  originalLabel = 'Original',
  modifiedLabel = 'Modified',
}: FullDocumentDiffViewerProps) {
  const content = useMemo(() => {
    // Reset key counter for each render
    keyCounter = 0;

    if (!originalHtml || !modifiedHtml) {
      return renderPlainTextDiff(originalText, modifiedText);
    }

    // Parse both HTML documents
    const parser = new DOMParser();
    const originalDoc = parser.parseFromString(originalHtml, 'text/html');
    const modifiedDoc = parser.parseFromString(modifiedHtml, 'text/html');

    // Extract blocks
    const originalBlocks = extractBlocks(originalDoc.body);
    const modifiedBlocks = extractBlocks(modifiedDoc.body);

    // Align using similarity-based LCS
    const alignment = alignBlocks(originalBlocks, modifiedBlocks);

    // Render
    const result: React.ReactNode[] = [];
    for (const op of alignment) {
      if (op.type === 'match' && op.original && op.modified) {
        result.push(renderMatchedBlock(op.original, op.modified));
      } else if (op.type === 'delete' && op.original) {
        result.push(renderBlockElement(op.original.element, 'deleted'));
      } else if (op.type === 'insert' && op.modified) {
        result.push(renderBlockElement(op.modified.element, 'added'));
      }
    }

    return result;
  }, [originalText, modifiedText, originalHtml, modifiedHtml]);

  // Compute stats from the alignment
  const stats = useMemo(() => {
    if (!originalHtml || !modifiedHtml) return null;

    const parser = new DOMParser();
    const origDoc = parser.parseFromString(originalHtml, 'text/html');
    const modDoc = parser.parseFromString(modifiedHtml, 'text/html');
    const origBlocks = extractBlocks(origDoc.body);
    const modBlocks = extractBlocks(modDoc.body);
    const alignment = alignBlocks(origBlocks, modBlocks);

    let matched = 0;
    let deleted = 0;
    let inserted = 0;
    let modified = 0;

    for (const op of alignment) {
      if (op.type === 'match') {
        if (op.similarity === 1) matched++;
        else modified++;
      } else if (op.type === 'delete') {
        deleted++;
      } else {
        inserted++;
      }
    }

    return { matched, modified, deleted, inserted, total: alignment.length };
  }, [originalHtml, modifiedHtml]);

  const hasChanges = stats
    ? stats.modified + stats.deleted + stats.inserted > 0
    : originalText.trim() !== modifiedText.trim();

  return (
    <div className="flex flex-col h-full">
      {/* Header bar */}
      <div className="shrink-0 flex items-center gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200">
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          Unified View
        </span>

        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            Deleted
          </span>
        </span>

        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
            Added
          </span>
        </span>

        <div className="flex-1" />

        {stats && (
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>{stats.total} blocks</span>
            {stats.modified > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                {stats.modified} modified
              </span>
            )}
            {stats.inserted > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400" />
                {stats.inserted} added
              </span>
            )}
            {stats.deleted > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                {stats.deleted} removed
              </span>
            )}
          </div>
        )}
      </div>

      {/* Version labels */}
      <div className="shrink-0 px-5 py-2 bg-white border-b border-slate-200 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
        {originalLabel} &rarr; {modifiedLabel}
      </div>

      {/* Diff content */}
      <div className="flex-1 overflow-auto bg-white">
        {!hasChanges ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-700">No differences found</p>
            <p className="text-xs text-slate-400 mt-1">The two versions are identical.</p>
          </div>
        ) : (
          <div className="p-5 text-sm leading-relaxed text-slate-700">
            {content}
          </div>
        )}
      </div>
    </div>
  );
}
